import { useState } from 'react'
import { APIProvider, Map, AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps'
import { ReactComponent as UnqualifyIcon } from '../../assets/arv/grid-unqualify2.svg'
import { ReactComponent as QualifiedIcon } from '../../assets/arv/qualified2.svg'

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
const MAP_ID   = 'c82c994f2480651a'
const DEFAULT_CENTER = { lat: 39.7392, lng: -104.9903 }

const fmtPrice = (n) =>
  n == null ? '—'
  : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n)

const fmtGlyph = (n) => {
  if (n == null) return '—'
  const rounded = n >= 1_000_000
    ? Math.round(n / 100_000) * 100_000
    : Math.round(n / 1_000)   * 1_000
  if (rounded >= 1_000_000) return '$' + (rounded / 1_000_000).toFixed(2).replace(/\.0$/, '') + 'M'
  return (rounded / 1_000) + 'K'
}

// ── Price pill marker (comp) ───────────────────────────────────────────────────
function CompMarker({ comp, active }) {
  const s = comp.status?.toLowerCase() ?? ''
  const statusColor = s === 'sold' ? '#4CBC18' : s === 'active' ? '#C13A4A' : '#F5C312'
  const bg = active ? '#17270D' : statusColor
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
      <div style={{
        background: bg,
        padding: '5px 10px',
        borderRadius: 999,
        boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
        whiteSpace: 'nowrap',
      }}>
        <span style={{ color: '#fff', fontSize: 12, fontWeight: 600, fontFamily: 'Figtree, sans-serif', lineHeight: 1 }}>
          {
            comp.status !== 'Sold' 
              ? fmtGlyph(comp.price)
              : fmtGlyph(comp.sold_price)
          }
        </span>
      </div>
      {/* Downward pointer */}
      <div style={{
        width: 0, height: 0,
        borderLeft: '5px solid transparent',
        borderRight: '5px solid transparent',
        borderTop: `6px solid ${bg}`,
      }} />
    </div>
  )
}

// ── Subject property marker ────────────────────────────────────────────────────
function SubjectMarker() {
  return (
    <div style={{
      width: 32, height: 32, borderRadius: '50%',
      background: '#17270D',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
    }}>
      <span style={{ color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: 'Figtree, sans-serif' }}>S</span>
    </div>
  )
}

// ── Info window card ───────────────────────────────────────────────────────────
function CompInfoWindow({ comp, comps, onListingClick, onGalleryOpen, onToggleArchived }) {
  const [bodyHovered, setBodyHovered] = useState(false)
  const [imgHovered, setImgHovered] = useState(false)
  const { street, city, state: stCode, zip } = comp.address ?? {}
  const addressLine = [street, city, stCode, zip].filter(Boolean).join(', ')
  const { beds, baths, sqft } = comp.specs ?? {}
  const photo = comp.photos?.[0]?.href
  const statusLower = comp.status?.toLowerCase() ?? ''
  const statusColor = statusLower === 'sold' ? '#3FAE16' : statusLower === 'active' ? '#C13A4A' : '#E8B611'
  const specsSegments = [
    beds  && { val: beds,                              label: ' bd' },
    baths && { val: baths,                             label: ' ba' },
    sqft  && { val: Number(sqft).toLocaleString(),     label: ' sqft' },
  ].filter(Boolean)

  const handleListingClick = (e) => {
    e.stopPropagation()
    const idx = comps.findIndex(c => c.propertyId === comp.propertyId)
    onListingClick?.(comp, idx)
  }

  const handlePhotoClick = (e) => {
    e.stopPropagation()
    const photos = comp.photos ?? []
    if (photos.length > 0) onGalleryOpen?.(photos)
  }

  return (
    // negative margin counters Google Maps InfoWindow default padding
    <div style={{ width: 270, borderRadius: 12, overflow: 'hidden', fontFamily: 'Figtree, sans-serif' }}>
      {/* Photo row */}
      <div style={{ position: 'relative', aspectRatio: '3/2', background: '#E7F0E2', overflow: 'hidden' }}>
        {photo
          ? <img
              src={photo} alt=""
              onClick={handlePhotoClick}
              onMouseEnter={() => setImgHovered(true)}
              onMouseLeave={() => setImgHovered(false)}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: comp.photos?.length > 0 ? 'pointer' : 'default', opacity: imgHovered ? .9 : 1 }}
            />
          : <div style={{ width: '100%', height: '100%', background: '#E7F0E2' }} />
        }
        {/* Status badge */}
        <span style={{
          position: 'absolute',   
          top: '15px', 
          left: '14px',
          background: statusColor, 
          color: '#fff',
          fontSize: 13, 
          padding: '2px 4px', 
          borderRadius: '.25rem',
          letterSpacing: '0.03em',
        }}>
          {(comp.status ?? '').toUpperCase()}
        </span>
        
      </div>

      {/* Body */}
      <div
        style={{ padding: '8px 8px 12px', background: '#fff', cursor: 'pointer' }}
        onClick={handleListingClick}
        onMouseEnter={() => setBodyHovered(true)}
        onMouseLeave={() => setBodyHovered(false)}
      >
        <p style={{ fontSize: 20, fontWeight: 800, color: '#17270D', margin: 0, lineHeight: 1.2 }}>
          { comp.status !== 'Sold' ? fmtPrice(comp.price): fmtPrice(comp.sold_price) }
        </p>
        <p style={{ fontSize: 14, fontWeight: 400, color: '#17270D', margin: '0 0 1px', lineHeight: 1.4 }}>
          {specsSegments.map((seg, i) => (
            <span key={i}>
              {i > 0 && <span> · </span>}
              <span style={{ fontWeight: 700 }}>{seg.val}</span>
              <span>{seg.label}</span>
            </span>
          ))}
        </p>
        <p style={{ fontSize: 14, fontWeight: 400, color: bodyHovered ? '#3AA015' : '#4F5F4A', margin: 0, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {addressLine}
        </p>
      </div>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function ArvMap({ coordinates, comps = [], onListingClick, onGalleryOpen, onToggleArchived, lastSearchedAt }) {
  const [activeCompId, setActiveCompId] = useState(null)
  const activeComp = comps.find(c => c.propertyId === activeCompId) ?? null
  const lastSearchLabel = lastSearchedAt
    ? `Last Search: ${new Date(lastSearchedAt).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
      })}`
    : null

  const center = coordinates?.lat && (coordinates?.lon ?? coordinates?.lng)
    ? { lat: coordinates.lat, lng: coordinates.lon ?? coordinates.lng }
    : DEFAULT_CENTER

  if (!MAPS_KEY) {
    return (
      <div
        className="w-full rounded-2xl bg-brand-muted flex items-center justify-center"
        style={{ height: 600, maxWidth: 1200 }}
      >
        <p className="text-[13px] text-brand-unselectedGray">
          Add <code className="bg-white px-[4px] py-[2px] rounded text-brand-dark">REACT_APP_GOOGLE_MAPS_API_KEY</code> to .env to enable the map
        </p>
      </div>
    )
  }

  return (
    <>
    {lastSearchLabel && (
      <p className="text-[11px] text-brand-secondary relative mt-[10px]">{lastSearchLabel}</p>
    )}
    <div className="w-full rounded-2xl overflow-hidden border border-[#DCEBD4]" style={{ height: 620, maxWidth: 1200 }}>
      <APIProvider apiKey={MAPS_KEY}>
        <Map
          mapId={MAP_ID}
          defaultCenter={center}
          defaultZoom={15}
          gestureHandling="greedy"
          fullscreenControl={false}
          mapTypeControl={false}
          renderingType='VECTOR'
          disableDefaultUI={true}
          style={{ width: '100%', height: '642px' }}
          onClick={() => setActiveCompId(null)}
        >
          {/* Subject property */}
          <AdvancedMarker position={center}>
            <SubjectMarker />
          </AdvancedMarker>

          {/* Comp markers */}
          {comps.map(comp =>
            comp.listingCoord?.lat && comp.listingCoord?.lng ? (
              <AdvancedMarker
                key={comp.propertyId}
                position={comp.listingCoord}
                onClick={() => setActiveCompId(prev =>
                  prev === comp.propertyId ? null : comp.propertyId
                )}
              >
                <CompMarker
                  comp={comp}
                  active={activeCompId === comp.propertyId}
                />
              </AdvancedMarker>
            ) : null
          )}

          {/* Info window */}
          {activeComp?.listingCoord && (
            <InfoWindow
              onCloseClick={() => setActiveCompId(null)}
              position={activeComp.listingCoord}
              pixelOffset={[0,-30]}
              headerDisabled={true}
              maxWidth={272}
              style={{overflowX:'hidden'}}
            >
              <CompInfoWindow
                comp={activeComp}
                comps={comps}
                onListingClick={onListingClick}
                onGalleryOpen={onGalleryOpen}
                onToggleArchived={onToggleArchived}
              />
            </InfoWindow>
          )}
        </Map>
      </APIProvider>
    </div>
    </>
  )
}
